import { SlowNotice } from '@pa/ui'
import { ArrowRight } from 'lucide-react'
import { useId, useRef, useState, type KeyboardEvent, type RefObject, type SyntheticEvent } from 'react'
import { ChallengeBox, Honeypot, useBotInputs, type BotInputs, type Recaptcha } from '@/features/bot'
import { dobIsValid, nameHasInvalidChars, nameIsValid, type IdentifyFields } from '../model/identify'
import { BackButton, ScreenTitle } from './EntryPanel'

export interface IdentifyFormProps {
  /** Kept after P-05 "Try again"; empty after P-04 (a fresh form). */
  initial: IdentifyFields
  pending: boolean
  /** The clinic's time zone: the DOB must be before the clinic's today. */
  timeZone: string
  /** Hidden with `existing=1` (UserFlows P-03). */
  onBack: (() => void) | undefined
  /** The bot inputs resolve once the score token is in (≤ 3 s, Frontend §4.6); the lookup waits for them. */
  onSubmit: (fields: IdentifyFields, bot: Promise<BotInputs>) => void
  /** The clinic's reCAPTCHA (task 16): the score token, and the checkbox for P-13. */
  recaptcha: Recaptcha
  /** P-13: `challenge_required` answered; the checkbox sits beside Continue until it is ticked. */
  challenge?: boolean
  /** P-13's widget can't load (after its one retry) → P-14. */
  onChallengeUnavailable?: () => void
}

const digits = (value: string, max: number) => value.replace(/\D/g, '').slice(0, max)

/**
 * P-03 Find your record (UserFlows P-03, Frontend §4.11), ported from the mockup's `site/Widget.tsx`
 * `IdentifyForm`. Continue stays disabled until both names are non-blank and valid and the DOB is a real past date
 * with a year after 1900; the only inline message is "Please use letters only". The DOB boxes take digits only and
 * move focus MM → DD → YYYY (Backspace in an empty box moves back). The details stay in React state only.
 */
export function IdentifyForm({
  initial,
  pending,
  timeZone,
  onBack,
  onSubmit,
  recaptcha,
  challenge = false,
  onChallengeUnavailable,
}: IdentifyFormProps) {
  const [fields, setFields] = useState<IdentifyFields>(initial)
  const bot = useBotInputs(recaptcha, 'lookup')
  const [challengeToken, setChallengeToken] = useState<string | null>(null)
  const [sent, setSent] = useState(0)
  const id = useId()
  const mmRef = useRef<HTMLInputElement>(null)
  const ddRef = useRef<HTMLInputElement>(null)
  const yyRef = useRef<HTMLInputElement>(null)

  const firstBad = nameHasInvalidChars(fields.first)
  const lastBad = nameHasInvalidChars(fields.last)
  const valid = nameIsValid(fields.first) && nameIsValid(fields.last) && dobIsValid(fields, timeZone)
  // P-13: Continue stays disabled until the box is ticked (UserFlows P-13).
  const ready = valid && (!challenge || challengeToken !== null)

  const set = (patch: Partial<IdentifyFields>) => {
    setFields((f) => ({ ...f, ...patch }))
  }
  // Backspace in an empty box moves focus back (Frontend §4.11).
  const backOnEmpty = (e: KeyboardEvent<HTMLInputElement>, prev: RefObject<HTMLInputElement | null>) => {
    if (e.key === 'Backspace' && e.currentTarget.value === '') prev.current?.focus()
  }

  const submit = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!ready || pending) return
    onSubmit(fields, bot.collect(challenge && challengeToken !== null ? challengeToken : undefined))
    // The checkbox token is single-use: a new challenge needs a new tick.
    if (challenge) setSent((n) => n + 1)
  }

  return (
    <form onSubmit={submit} noValidate>
      {onBack && <BackButton onClick={onBack} />}
      <ScreenTitle>Let&apos;s find your record</ScreenTitle>
      <p className="wg-text">Enter your details exactly as they appear on your record.</p>
      <Honeypot {...bot.honeypot} />
      <div className="wg-fields">
        <div className="wg-row2">
          <div className="field">
            <label className="label" htmlFor={`${id}-first`}>
              First name
              <span className="req" aria-hidden>
                *
              </span>
            </label>
            <input
              id={`${id}-first`}
              className="input"
              autoComplete="given-name"
              maxLength={50}
              aria-required
              aria-invalid={firstBad}
              aria-describedby={firstBad ? `${id}-first-err` : undefined}
              value={fields.first}
              onChange={(e) => {
                set({ first: e.target.value })
              }}
            />
            {firstBad && (
              <span id={`${id}-first-err`} className="field-error" role="alert">
                Please use letters only
              </span>
            )}
          </div>
          <div className="field">
            <label className="label" htmlFor={`${id}-last`}>
              Last name
              <span className="req" aria-hidden>
                *
              </span>
            </label>
            <input
              id={`${id}-last`}
              className="input"
              autoComplete="family-name"
              maxLength={50}
              aria-required
              aria-invalid={lastBad}
              aria-describedby={lastBad ? `${id}-last-err` : undefined}
              value={fields.last}
              onChange={(e) => {
                set({ last: e.target.value })
              }}
            />
            {lastBad && (
              <span id={`${id}-last-err`} className="field-error" role="alert">
                Please use letters only
              </span>
            )}
          </div>
        </div>
        <div className="field" role="group" aria-labelledby={`${id}-dob`}>
          <span className="label" id={`${id}-dob`}>
            Date of birth
            <span className="req" aria-hidden>
              *
            </span>
          </span>
          <div className="dob-inputs">
            <input
              ref={mmRef}
              className="input"
              inputMode="numeric"
              autoComplete="bday-month"
              placeholder="MM"
              aria-label="Month"
              value={fields.mm}
              onChange={(e) => {
                const mm = digits(e.target.value, 2)
                set({ mm })
                if (mm.length === 2) ddRef.current?.focus()
              }}
            />
            <span className="sep" aria-hidden>
              /
            </span>
            <input
              ref={ddRef}
              className="input"
              inputMode="numeric"
              autoComplete="bday-day"
              placeholder="DD"
              aria-label="Day"
              value={fields.dd}
              onKeyDown={(e) => {
                backOnEmpty(e, mmRef)
              }}
              onChange={(e) => {
                const dd = digits(e.target.value, 2)
                set({ dd })
                if (dd.length === 2) yyRef.current?.focus()
              }}
            />
            <span className="sep" aria-hidden>
              /
            </span>
            <input
              ref={yyRef}
              className="input dob-year"
              inputMode="numeric"
              autoComplete="bday-year"
              placeholder="YYYY"
              aria-label="Year"
              value={fields.yyyy}
              onKeyDown={(e) => {
                backOnEmpty(e, ddRef)
              }}
              onChange={(e) => {
                set({ yyyy: digits(e.target.value, 4) })
              }}
            />
          </div>
        </div>
      </div>
      {challenge && (
        <ChallengeBox
          recaptcha={recaptcha}
          action="lookup"
          resetKey={sent}
          onToken={setChallengeToken}
          onUnavailable={() => {
            onChallengeUnavailable?.()
          }}
        />
      )}
      <button
        type="submit"
        className="btn btn-accent btn-lg btn-block wg-continue"
        disabled={!ready || pending}
        aria-describedby={!ready && !pending ? `${id}-why` : undefined}
      >
        {pending ? (
          <>
            <span className="spinner" aria-hidden /> Looking you up…
          </>
        ) : (
          <>
            Continue <ArrowRight aria-hidden />
          </>
        )}
      </button>
      <SlowNotice pending={pending} />
      {!ready && !pending && (
        <span id={`${id}-why`} className="sr-only">
          {valid
            ? 'Tick the box to confirm you’re not a robot, then continue.'
            : 'Enter your first name, last name and date of birth to continue.'}
        </span>
      )}
    </form>
  )
}
